import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { newIdempotencyKey } from "./api";

function subscribeOnline(callback: () => void) {
  window.addEventListener("online", callback);
  window.addEventListener("offline", callback);
  return () => {
    window.removeEventListener("online", callback);
    window.removeEventListener("offline", callback);
  };
}

const NON_TEXT_INPUTS = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"]);

function isTextField(el: Element | null): el is HTMLElement {
  if (!el) return false;
  if (el.tagName === "TEXTAREA") return true;
  if (el.tagName === "INPUT") return !NON_TEXT_INPUTS.has((el as HTMLInputElement).type);
  return (el as HTMLElement).isContentEditable;
}

/** En dessous, une baisse de hauteur visible n'est pas un clavier (barre d'adresse, etc.). */
const KEYBOARD_MIN_HEIGHT = 120;

/**
 * Clavier du téléphone : publie sa hauteur (--kb) et la hauteur visible (--vvh) pour que les panneaux
 * du bas remontent au-dessus (iPhone), marque la page « kb-open » pendant la saisie et garde le champ actif visible.
 * Sur Android, la balise viewport (interactive-widget=resizes-content) redimensionne déjà la page : --kb y reste à 0.
 */
export function useKeyboardAware() {
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    const touch = window.matchMedia?.("(pointer: coarse)").matches ?? false;
    let timer = 0;

    const reveal = (delay: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const el = document.activeElement;
        if (isTextField(el)) el.scrollIntoView({ block: "center", behavior: "smooth" });
      }, delay);
    };
    // Hauteur visible sans clavier, pour la largeur actuelle (elle change si on tourne le téléphone).
    let fullHeight = 0;
    let fullWidth = 0;
    const measure = () => {
      const height = vv?.height ?? window.innerHeight;
      const kb = vv ? Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)) : 0;
      root.style.setProperty("--kb", `${kb}px`);
      root.style.setProperty("--vvh", `${Math.round(height)}px`);
      const width = vv?.width ?? window.innerWidth;
      if (Math.abs(width - fullWidth) > 40) {
        fullWidth = width;
        fullHeight = height;
      }
      fullHeight = Math.max(fullHeight, height);
      return height;
    };
    const onResize = () => {
      const height = measure();
      // Le clavier peut se fermer sans quitter le champ (flèche ou geste retour sur Android) :
      // on se fie à la place qu'il prend à l'écran pour remontrer les boutons du bas.
      if (fullHeight - height < KEYBOARD_MIN_HEIGHT) root.classList.remove("kb-open");
      else if (touch && isTextField(document.activeElement)) root.classList.add("kb-open");
      reveal(60);
    };
    const onFocusIn = () => {
      if (!isTextField(document.activeElement)) return;
      if (touch) root.classList.add("kb-open");
      reveal(320);
    };
    const onFocusOut = () => {
      window.setTimeout(() => {
        if (!isTextField(document.activeElement)) root.classList.remove("kb-open");
      }, 0);
    };

    measure();
    vv?.addEventListener("resize", onResize);
    vv?.addEventListener("scroll", measure);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      window.clearTimeout(timer);
      vv?.removeEventListener("resize", onResize);
      vv?.removeEventListener("scroll", measure);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      root.classList.remove("kb-open");
    };
  }, []);
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}

const DRAFT_PREFIX = "tp:draft:";

function readStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/**
 * État conservé dans le navigateur tant qu'il n'est pas validé :
 * une coupure réseau, une fermeture d'onglet ou un retour arrière ne font pas perdre la saisie.
 */
export function useDraft<T>(key: string | null, initial: T) {
  const [value, setValue] = useState<T>(() => (key ? (readStorage<T>(key) ?? initial) : initial));
  const restored = useRef(key ? readStorage<T>(key) !== null : false);

  useEffect(() => {
    if (!key) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_PREFIX + key, JSON.stringify(value));
      } catch {
        /* stockage plein ou indisponible : on garde l'état en mémoire */
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [key, value]);

  const clear = useCallback(() => {
    if (key) {
      try {
        localStorage.removeItem(DRAFT_PREFIX + key);
      } catch {
        /* rien */
      }
    }
    restored.current = false;
  }, [key]);

  return { value, setValue, clear, restored: restored.current };
}

export function clearDraft(key: string) {
  try {
    localStorage.removeItem(DRAFT_PREFIX + key);
  } catch {
    /* rien */
  }
}

/** Clé d'idempotence stable pendant la vie d'un formulaire, renouvelée après succès. */
export function useIdempotencyKey() {
  const ref = useRef(newIdempotencyKey());
  return {
    get key() {
      return ref.current;
    },
    renew() {
      ref.current = newIdempotencyKey();
    }
  };
}

export function useScrolled(threshold = 4): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

export function usePersistentState<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(`tp:pref:${key}`);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(`tp:pref:${key}`, JSON.stringify(value));
    } catch {
      /* rien */
    }
  }, [key, value]);
  return [value, setValue] as const;
}

/** Normalise pour une recherche insensible à la casse et aux accents. */
export function normalizeSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}
